import {
  WEBINAR_STEPS,
  WEBINAR_STEP_LABELS,
  type Webinar,
} from "@xop/dashboard-shared";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Alert,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import { useCan } from "../../../auth/permissions.js";
import { EmptyState } from "../../../components/ui/empty-state.js";
import { PageHeader } from "../../../components/ui/page-header.js";
import { NewWebinarDialog } from "./NewWebinarDialog.js";
import { webinarsApi } from "./client.js";

/**
 * Marketing ▸ Webinars — the list.
 *
 * One card per event, showing the ONE thing an operator wants from a list:
 * how far through the five steps it is and what to do next. Clicking a card
 * opens the wizard on that next step.
 */

export function WebinarsListPage(): JSX.Element {
  const navigate = useNavigate();
  const canEdit = useCan("marketing:edit");
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["webinars", "list"],
    queryFn: () => webinarsApi.list(),
  });

  return (
    <Box sx={{ p: 3, display: "grid", gap: 3 }}>
      <PageHeader
        title="Webinars"
        description="Run a webinar end to end: invite people, thank the ones who came, turn the recording into content, ask how it went, and find new leads."
        actions={
          canEdit ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              data-testid="new-webinar"
            >
              New webinar
            </Button>
          ) : null
        }
      />

      {error && (
        <Alert severity="error">
          Could not load your webinars. {(error as Error)?.message ?? ""}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<EventIcon sx={{ fontSize: 40 }} />}
          title="No webinars yet"
          description="A webinar walks through five simple steps, one at a time. Start by giving it a name and a date - you can fill in everything else later."
          action={
            canEdit ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                Create your first webinar
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Box sx={{ display: "grid", gap: 2 }}>
          {data!.map((w) => (
            <WebinarCard key={w.id} webinar={w} onOpen={() => navigate(`${w.id}`)} />
          ))}
        </Box>
      )}

      <NewWebinarDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(w) => {
          void refetch();
          navigate(`${w.id}`);
        }}
      />
    </Box>
  );
}

function WebinarCard({
  webinar,
  onOpen,
}: {
  webinar: Webinar;
  onOpen: () => void;
}): JSX.Element {
  const doneCount = webinar.completed_steps.length;
  const total = WEBINAR_STEPS.length;
  const nextLabel = webinar.next_step ? WEBINAR_STEP_LABELS[webinar.next_step] : null;
  const stepNumber = webinar.next_step ? WEBINAR_STEPS.indexOf(webinar.next_step) + 1 : total;

  return (
    <Card variant="outlined" data-testid={`webinar-card-${webinar.id}`}>
      <CardActionArea onClick={onOpen} sx={{ p: 2.5, display: "block" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { sm: "center" },
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{webinar.title}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.25 }}>
              {webinar.scheduled_at ? formatWhen(webinar.scheduled_at) : "No date set yet"}
            </Typography>
          </Box>
          <StatusChip webinar={webinar} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(doneCount / total) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.75 }}>
            {nextLabel
              ? `Step ${stepNumber} of ${total} - ${nextLabel}`
              : `All ${total} steps done`}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}

function StatusChip({ webinar }: { webinar: Webinar }): JSX.Element {
  if (webinar.status === "complete") {
    return <Chip size="small" color="success" label="Finished" />;
  }
  if (webinar.status === "running") {
    return <Chip size="small" color="primary" label="In progress" />;
  }
  return <Chip size="small" variant="outlined" label="Not started" />;
}

/** "1 September 2026, 3:00 PM UTC" — the same UTC-explicit rule the invite
 * email uses, so the list and the email can never show different hours. */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No date set yet";
  const date = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${date}, ${time} UTC`;
}
